import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import colors from "styles/colors";
import Icon from "new_components/atoms/Icon";
import typography, { applyTypography } from "styles/typography";
import PresetPromptCard from "new_components/molecules/PresetPromptCard"; // Import the new card component

import Tooltip from "new_components/molecules/Tooltip";

/** STYLES **/

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LabelWrapper = styled.label`
  ${applyTypography(typography.body.regular)}
  color: ${colors.text.dark};
  margin-bottom: 0.25rem;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 25px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 0px 1px;
  background-color: ${colors.background.white};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus-within {
    box-shadow: rgba(0, 0, 0, 0.25) 0px 0px 0px 1px;
  }
  &.error {
    border-color: ${colors.auxiliary.red[500]};
  }
`;

const DisabledWrapper = styled.div`
  border-radius: 25px;

  &.disabled {
    background-color: ${colors.background.white};
    color: ${colors.neutral[600]};
    cursor: not-allowed;

    textarea {
      background-color: ${colors.background.white};
      cursor: not-allowed;
      border-radius: 25px;
    }
    svg {
      cursor: not-allowed;
    }
  }
`;

const TopActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem;
  border-bottom: 1px solid ${colors.neutral[300]};
`;

const BottomActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 20px;
  padding-bottom: 10px;
  padding-left: 20px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 3px;

  ${(props) =>
    props.isDragging &&
    `
    background-color: ${colors.primary[100]};
    border-color: ${colors.primary[500]};
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
  `}
  ${(props) =>
    props.isInvalidFile &&
    `
    background-color: ${colors.auxiliary.red[100]};
    border-color: ${colors.auxiliary.red[500]};
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
  `}
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 0rem 1.4rem;
  padding-top: ${(props) => (props.chatStarted ? "15px" : "10px")};
  ${applyTypography(typography.body.regular)}
  background-color: transparent;
  ${(props) =>
    props.isDragging &&
    `
    background-color: ${colors.primary[100]} !important;
  `}
  ${(props) =>
    props.isInvalidFile &&
    `
    background-color: ${colors.auxiliary.red[100]} !important;
  `}
  color: ${colors.text.dark};
  border: none;
  outline: none;
  resize: none; /* prevents manual drag-resizing */
  overflow: hidden; /* hide scrollbar to enable auto-expanding */
  line-height: 1.5;
  white-space: pre-wrap;
  max-height: 200px; /* limit max height */
  overflow-y: auto; /* enable vertical scrolling if needed */
  overflow-wrap: break-word;
  &::placeholder {
    color: ${colors.neutral[600]};
  }
  &:focus {
    outline: none;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.dark};
`;

const ErrorMessage = styled.span`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: ${colors.auxiliary.red[500]};
`;

/** COMPONENT **/
const DescriptionInput = React.forwardRef(
  (
    {
      label,
      id,
      placeholder,
      icon,
      topActionRow,
      bottomActionRow,
      error,
      className,
      value,
      onChange,
      handleEnter,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      isDragging,
      selectedCards,
      isInvalidFile,
      sourceCards,
      chatStarted = false,
      uploadRef,
      sendEnabled = true,
      inputEnabled = true,
      clearPrompt,
      presetPrompt,
      fileUploadEnabled = true,
      ...props
    },
    ref
  ) => {
    const textAreaRef = useRef(null);

    useEffect(() => {
      if (textAreaRef.current) {
        // Reset height to auto to shrink if necessary
        textAreaRef.current.style.height = "auto";
        // Set height based on scrollHeight
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      }
    }, [value]);

    const handleChange = (e) => {
      if (onChange) onChange(e);
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && handleEnter) {
        // Handle shift + enter, add a new line
        if (e.shiftKey) {
          e.preventDefault();
          const { selectionStart, selectionEnd } = e.target;
          const newValue =
            value.substring(0, selectionStart) +
            "\n" +
            value.substring(selectionEnd);
          onChange({ target: { value: newValue } });
          return;
        }

        e.preventDefault();
        handleEnter();
      }
    };

    const inputContainer = (
      <Container className={className}>
        {fileUploadEnabled && uploadRef}
        {label && <LabelWrapper htmlFor={id}>{label}</LabelWrapper>}
        <Wrapper className={`${error ? "error" : ""}`}>
          {topActionRow && <TopActionRow>{topActionRow}</TopActionRow>}
          <DisabledWrapper className={`${!inputEnabled ? "disabled" : ""}`}>
            <InputContainer
              isDragging={fileUploadEnabled && isDragging}
              onDragOver={fileUploadEnabled && handleDragOver}
              onDragLeave={fileUploadEnabled && handleDragLeave}
              onDrop={fileUploadEnabled && handleDrop}
              isInvalidFile={fileUploadEnabled && isInvalidFile}
            >
              {sourceCards && !chatStarted && sourceCards}
              {(presetPrompt && (
                <PresetPromptCard
                  clearPrompt={clearPrompt}
                  prompt={presetPrompt?.categoryContent?.props?.prompt?.title}
                />
              )) || (
                <StyledTextArea
                  id={id}
                  ref={textAreaRef}
                  placeholder={placeholder}
                  disabled={!inputEnabled}
                  chatStarted={chatStarted}
                  value={value}
                  isDragging={isDragging}
                  isInvalidFile={isInvalidFile}    
                  onChange={inputEnabled && handleChange}
                  onKeyDown={inputEnabled && handleKeyPress} // Add handleKeyPress here
                  {...props}
                />
              )}
              {icon && (
                <IconWrapper>
                  <Icon name={icon} size={20} />
                </IconWrapper>
              )}
            </InputContainer>
          </DisabledWrapper>
          {bottomActionRow && (
            <BottomActionRow>{bottomActionRow}</BottomActionRow>
          )}
        </Wrapper>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Container>
    );

    if (!inputEnabled) {
      return (
        <Tooltip tooltipText="You must first add a data source.">
          {inputContainer}
        </Tooltip>
      );
    }

    return inputContainer;
  }
);

DescriptionInput.displayName = "DescriptionInput";

DescriptionInput.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  topActionRow: PropTypes.node,
  bottomActionRow: PropTypes.node,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default DescriptionInput;
